import { useEffect, useMemo, useRef } from 'react'
import jwtDecode from 'jwt-decode'
import { useStore } from '../models'
import { useSessionStore } from '../models/session/useSessionStore'

const ROLES_CLAIM = 'https://pokerpower.com/roles'

type UserRole = 'user' | 'teacher' | 'admin'

interface DecodedToken {
  [ROLES_CLAIM]?: UserRole[]
}

export function useUserRoles(): UserRole[] {
  const idToken = useSessionStore((state) => state.tokenConfig?.idToken)
  const store = useStore()
  const mongoRoles = (store.session.user?.userRoles ?? []) as UserRole[]
  const lastTelemetrySource = useRef<string | null>(null)

  const jwtRoles = useMemo(() => {
    if (!idToken) return undefined
    try {
      const decoded = jwtDecode<DecodedToken>(idToken)
      const claim = decoded[ROLES_CLAIM]
      return Array.isArray(claim) && claim.length > 0 ? claim : undefined
    } catch {
      return undefined
    }
  }, [idToken])

  useEffect(() => {
    const source = jwtRoles ? 'jwt' : mongoRoles.length > 0 ? 'mongo_fallback' : 'none'
    if (lastTelemetrySource.current === source) return
    lastTelemetrySource.current = source
    store.analytics.setEvent('Roles Resolved', { source })
  }, [jwtRoles, mongoRoles.length, store.analytics])

  return jwtRoles ?? mongoRoles
}

export function useIsAdmin(): boolean {
  const roles = useUserRoles()
  return roles.includes('admin')
}
